import { useQuery } from "@apollo/client";
import Modal from "components/layout/modal";
import { listClaimAttachments } from "graphql/queries/claims";
import {
    ListClaimAttachmentsQuery,
    ListClaimAttachmentsQueryVariables,
} from "graphql/queries/__generated__/claims";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface FileItem {
    id: string;
    name: string;
    url: string;
    size?: number;
    uploadedAt?: string;
}

interface ClaimAttachmentsViewProps {
    claimId: string;
}

const ClaimAttachmentsView: React.FC<ClaimAttachmentsViewProps> = ({
    claimId,
}) => {
    const { t } = useTranslation();
    const [selectedPdf, setSelectedPdf] = useState<string | null>(null);

    const { data, loading, error } = useQuery<
        ListClaimAttachmentsQuery,
        ListClaimAttachmentsQueryVariables
    >(listClaimAttachments, {
        variables: { claimId },
        skip: !claimId,
    });

    const attachments: FileItem[] =
        data?.listClaimAttachments?.data?.attachments?.map((att) => ({
            id: att?.id || "",
            name: att?.file_name || "Unknown File",
            url: att?.file_url || "",
            size: att?.file_size || undefined,
            uploadedAt: att?.uploaded_at || undefined,
        })) || [];

    const formatFileSize = (bytes?: number): string => {
        if (!bytes) return "";
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[800px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">
                        {t("claims_page.documents.loading")}
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-[800px]">
                <div className="text-center text-red-600">
                    <p>
                        {t("claims_page.documents.error")}: {error.message}
                    </p>
                </div>
            </div>
        );
    }

    if (attachments.length === 0) {
        return (
            <div className="flex justify-center items-center flex-col h-[800px]">
                <svg
                    className="w-20 h-20 text-gray-300 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                </svg>
                <h1 className="text-sm md:text-xl font-semibold mt-2">
                    {t("claims_page.documents.no_attachments")}
                </h1>
                <p className="text-xs md:text-lg font-extralight">
                    {t("claims_page.documents.no_documents_uploaded")}
                </p>
            </div>
        );
    }

    return (
        <div className="h-[800px] flex gap-4">
            {/* PDF List Sidebar */}
            <div className="w-1/3 border-r pr-4 overflow-y-auto">
                <h3 className="font-semibold mb-4 text-gray-700">
                    Available Documents ({attachments.length})
                </h3>
                <div className="space-y-2">
                    {attachments.map((file) => (
                        <button
                            key={file.id}
                            onClick={() => setSelectedPdf(file.url)}
                            className={`w-full p-3 rounded-lg border transition-colors text-left ${
                                selectedPdf === file.url
                                    ? "bg-blue-50 border-blue-300 text-blue-700"
                                    : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                            }`}>
                            <div className="font-medium text-sm truncate">
                                {file.name}
                            </div>
                            {file.size && (
                                <div className="text-xs text-gray-500 mt-1">
                                    {formatFileSize(file.size)}
                                </div>
                            )}
                            {file.uploadedAt && (
                                <div className="text-xs text-gray-400 mt-0.5">
                                    {new Date(
                                        file.uploadedAt,
                                    ).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                    })}
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* PDF Viewer */}
            <div className="flex-1 h-full">
                {selectedPdf ? (
                    <div className="h-full rounded-lg overflow-hidden">
                        <iframe
                            src={selectedPdf}
                            width="100%"
                            height="100%"
                            className="border-none"
                            title={t("modal_titles.pdf_viewer")}
                        />
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        <div className="text-center">
                            <svg
                                className="w-16 h-16 mx-auto mb-4 text-gray-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            <p>{t("documents.select_document")}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

interface ClaimDocumentViewerContainerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    claimId: string;
}

const ClaimDocumentViewerContainer = ({
    open,
    setOpen,
    claimId,
}: ClaimDocumentViewerContainerProps) => {
    const { t } = useTranslation();
    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title={t("modal_titles.claim_documents")}
            description="View all supporting documents for this claim"
            size="6xl">
            <div className="flex flex-col h-full">
                <div className="flex-1 p-4">
                    <ClaimAttachmentsView claimId={claimId} />
                </div>
            </div>
        </Modal>
    );
};

export default ClaimDocumentViewerContainer;
